import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// "Tapas time is back — stay tuned" teaser. No end date yet; remove this popup
// (component + its wiring in SlowPlateLayout + the image) once tapas service
// actually launches.
//
// Styled for the Slow Plate homepage: the poster sits in a bone mat like a
// framed print, captioned with a link down to the Tapas Edit. Colors are
// literal rather than tokens so it renders the same wherever it's mounted.
const TapasTimePopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const lastFocus = useRef<Element | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // sessionStorage (not localStorage) so it shows once per visit rather than
    // on every navigation within that visit.
    const dismissed = sessionStorage.getItem('tapasTimePopupDismissed') === 'true';
    if (!dismissed) {
      lastFocus.current = document.activeElement;
      setShowPopup(true);
    }
  }, []);

  const handleClose = () => {
    sessionStorage.setItem('tapasTimePopupDismissed', 'true');
    setShowPopup(false);
    if (lastFocus.current instanceof HTMLElement) lastFocus.current.focus();
  };

  useEffect(() => {
    if (!showPopup) return;
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showPopup]);

  const ease = [0.16, 1, 0.3, 1] as const;

  return (
    <AnimatePresence>
      {showPopup && (
        <motion.div
          className="sp-popup"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease }}
          onClick={handleClose}
        >
          <motion.div
            ref={dialogRef}
            tabIndex={-1}
            className="sp-popup-card"
            role="dialog"
            aria-modal="true"
            aria-label="Tapas time is back"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.9, ease }}
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" onClick={handleClose} className="sp-popup-x" aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
              </svg>
            </button>

            <img
              src="/assets/images/tapas-time-popup.webp"
              alt="Tapas time is back — stay tuned"
              width={800}
              height={1036}
            />
            <div className="sp-popup-cap">
              <b>Tapas is back.</b>
              <a className="sp-arrow-link" href="#tapas" onClick={handleClose}>
                <span>See the lineup</span>
                <svg className="sp-arr" viewBox="0 0 34 12" aria-hidden="true">
                  <path d="M0 6h32" />
                  <path d="M27 1l5 5-5 5" />
                </svg>
              </a>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TapasTimePopup;
